import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import CredentialEditorButton from "../components/CredentialEditorButton";
import PartnerBrandModal from "../components/PartnerBrandModal";
import { Handshake, Plus, Trash2, KeyRound, Check, X, DollarSign, Send, ChevronDown, ChevronUp, Save, Pencil, Copy, FileText, ArrowUpDown, Receipt, Palette, Upload, Download, BookOpen, Mail, MessageCircle, AlertOctagon, RotateCcw, Lock } from "lucide-react";
import FounderCancellations from "../components/FounderCancellations";

const USD = (n) => `$${Number(n || 0).toLocaleString()}`;
const CUR_SYM = { USD: "$", GBP: "£", EUR: "€" };
const PROGRAMS = ["Volunteering", "Internship", "High School Exchange", "Student Group Tours", "Cultural Immersion", "Language Study", "Other"];

// Turn a partner email into a mailto link + a phone into a WhatsApp link.
// WhatsApp's wa.me endpoint wants a pure digit string (with country code, no
// spaces / dashes / plus signs), so we strip everything non-numeric. If the
// value is missing we render an em-dash instead of a broken link.
const MailLink = ({ email, className = "" }) => email
  ? <a href={`mailto:${email}`} className={`text-brand hover:underline inline-flex items-center gap-1 ${className}`} onClick={(e) => e.stopPropagation()} title={`Email ${email}`}><Mail size={11}/> {email}</a>
  : <span className="text-[color:var(--aii-muted)]">—</span>;
const WhatsAppLink = ({ phone, className = "" }) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return <span className="text-[color:var(--aii-muted)]">—</span>;
  return <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" className={`text-emerald-700 hover:underline inline-flex items-center gap-1 ${className}`} onClick={(e) => e.stopPropagation()} title={`WhatsApp ${phone}`}><MessageCircle size={11}/> {phone}</a>;
};

// Small round avatar for an applicant. Falls back to first-letter
// initial when the applicant hasn't uploaded a profile pic yet.
function ApplicantAvatar({ picId, name, size = 40 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  if (picId) {
    return (
      <img src={`${API}/files/${picId}?auth=${getToken()}`} alt=""
           className="rounded-full object-cover border-2 border-[color:var(--aii-green-tint)] flex-shrink-0"
           style={{ width: size, height: size }}/>
    );
  }
  return (
    <div className="rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center flex-shrink-0"
         style={{ width: size, height: size, fontSize: Math.max(12, size * 0.4) }}>{initial}</div>
  );
}

function PricingPanel({ partner, allPartners }) {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [copySource, setCopySource] = useState("");
  const [copying, setCopying] = useState(false);
  const empty = { destination: partner.partner_profile?.country || "", city: "", program: "Volunteering", duration_weeks: 4, price: "", currency: "USD" };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [r, c] = await Promise.all([
      api.get("/pricing").catch(() => ({ data: [] })),
      api.get("/accommodations/countries").catch(() => ({ data: { countries: [] } })),
    ]);
    setRows((r.data || []).filter(x => x.partner_id === partner.id));
    setCountries(c.data?.countries || ["India", "Nepal", "Sri Lanka"]);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.destination) return toast.error("Country required");
    if (!form.price || parseFloat(form.price) <= 0) return toast.error("Enter a price");
    try {
      const payload = { ...form, partner_id: partner.id, price: parseFloat(form.price), duration_weeks: parseInt(form.duration_weeks) || 0 };
      if (editingId) await api.patch(`/pricing/${editingId}`, payload);
      else await api.post("/pricing", payload);
      toast.success(editingId ? "Updated" : "Rule added");
      setForm(empty); setEditingId(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyFrom = async () => {
    if (!copySource) return toast.error("Pick a partner to copy from");
    if (copySource === partner.id) return toast.error("Choose a different partner");
    setCopying(true);
    try {
      const { data } = await api.post("/pricing/copy", { source_partner_id: copySource, target_partner_id: partner.id });
      if (data.copied === 0 && data.skipped === 0) toast.info(data.reason || "Nothing to copy");
      else toast.success(`Copied ${data.copied} rule${data.copied === 1 ? "" : "s"}${data.skipped > 0 ? ` · ${data.skipped} skipped (already present)` : ""}`);
      setCopySource(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCopying(false); }
  };

  const edit = (r) => {
    setEditingId(r.id);
    setForm({ destination: r.destination, city: r.city || "", program: r.program, duration_weeks: r.duration_weeks, price: r.price, currency: r.currency });
  };
  const del = async (id) => {
    if (!window.confirm("Delete this pricing rule?")) return;
    try { await api.delete(`/pricing/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const copySourceOptions = (allPartners || []).filter(p => p.id !== partner.id);

  return (
    <div className="mt-3 border-t border-[color:var(--aii-border)] pt-3 space-y-3 bg-[color:var(--aii-bg)] p-3 rounded" data-testid={`pricing-panel-${partner.id}`}>
      <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold flex items-center gap-1.5">
        <DollarSign size={12} className="text-brand"/> Pricing rules for {partner.name}
      </div>

      {copySourceOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-[color:var(--aii-green-tint)] border border-[color:var(--aii-border)] rounded-md p-2 text-xs">
          <Copy size={13} className="text-brand"/>
          <span className="font-semibold">Copy pricing from another partner:</span>
          <select data-testid={`pr-copy-src-${partner.id}`} value={copySource} onChange={e => setCopySource(e.target.value)} className="input-aii text-sm py-1 flex-1 min-w-[180px]" style={{ maxWidth: 260 }}>
            <option value="">Choose partner…</option>
            {copySourceOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={copyFrom} disabled={!copySource || copying} data-testid={`pr-copy-do-${partner.id}`}
                  className="text-xs px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
            <Copy size={11}/> {copying ? "Copying…" : "Copy rules"}
          </button>
          <span className="text-[color:var(--aii-muted)]">— duplicates are auto-skipped</span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 items-end">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[10px] uppercase text-[color:var(--aii-muted)]">Country *</label>
          <select data-testid={`pr-country-${partner.id}`} className="input-aii text-sm" value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })}>
            <option value="">Choose…</option>
            {countries.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-[color:var(--aii-muted)]">City</label>
          <input data-testid={`pr-city-${partner.id}`} className="input-aii text-sm" placeholder="Any" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}/>
        </div>
        <div>
          <label className="text-[10px] uppercase text-[color:var(--aii-muted)]">Program</label>
          <select data-testid={`pr-program-${partner.id}`} className="input-aii text-sm" value={form.program} onChange={e => setForm({ ...form, program: e.target.value })}>
            {PROGRAMS.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-[color:var(--aii-muted)]">Weeks</label>
          <input data-testid={`pr-dur-${partner.id}`} type="number" min={0} className="input-aii text-sm" value={form.duration_weeks} onChange={e => setForm({ ...form, duration_weeks: e.target.value })}/>
        </div>
        <div>
          <label className="text-[10px] uppercase text-[color:var(--aii-muted)]">Price *</label>
          <input data-testid={`pr-price-${partner.id}`} type="number" min={0} className="input-aii text-sm" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}/>
        </div>
        <div>
          <label className="text-[10px] uppercase text-[color:var(--aii-muted)]">Currency</label>
          <select data-testid={`pr-cur-${partner.id}`} className="input-aii text-sm" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
            <option>USD</option><option>GBP</option><option>EUR</option>
          </select>
        </div>
        <div className="flex gap-1">
          <button onClick={save} data-testid={`pr-save-${partner.id}`} className="text-xs px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1"><Save size={11}/> {editingId ? "Save" : "Add"}</button>
          {editingId && <button onClick={() => { setEditingId(null); setForm(empty); }} className="text-xs px-2 py-1.5 border rounded"><X size={11}/></button>}
        </div>
      </div>

      {rows.length > 0 && (
        <table className="aii-table text-xs">
          <thead><tr><th>Country · City</th><th>Program</th><th>Weeks</th><th>Price</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`pr-row-${r.id}`}>
                <td>{r.destination}{r.city ? ` · ${r.city}` : " · Any city"}</td>
                <td>{r.program}</td>
                <td>{r.duration_weeks}</td>
                <td className="font-semibold text-brand">{CUR_SYM[r.currency] || r.currency}{Number(r.price).toLocaleString()}</td>
                <td className="text-right">
                  <button onClick={() => edit(r)} className="text-xs px-2 py-0.5 border rounded mr-1 inline-flex items-center gap-1"><Pencil size={10}/> Edit</button>
                  <button onClick={() => del(r.id)} data-testid={`pr-del-${r.id}`} className="text-xs px-2 py-0.5 border border-red-300 text-red-700 rounded inline-flex items-center gap-1"><Trash2 size={10}/> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length === 0 && (
        <div className="text-xs text-[color:var(--aii-muted)] italic">No pricing rules yet — add one above, or copy from another partner to bootstrap quickly.</div>
      )}
    </div>
  );
}

function CreateAccount({ onCreated }) {
  const [f, setF] = useState({ name: "", email: "", country: "", city: "", address: "", website: "", contact_person: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email || !f.country || !f.city) return toast.error("Name / email / country / city required");
    setBusy(true);
    try {
      const { data } = await api.post("/partner-accounts", f);
      const msg = `Partner login\nEmail: ${data.email}\nPassword: ${data.password}`;
      try { await navigator.clipboard.writeText(msg); toast.success("Created · credentials copied"); }
      catch { toast.success(`Created — password: ${data.password}`); }
      setF({ name: "", email: "", country: "", city: "", address: "", website: "", contact_person: "", phone: "", notes: "" });
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="aii-card p-5 space-y-2" data-testid="partner-create">
      <h3 className="font-semibold flex items-center gap-2"><Plus size={16} className="text-brand"/> Add partner account</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input className="input-aii" placeholder="Organisation name *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="pa-name"/>
        <input className="input-aii" placeholder="Email *" type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="pa-email"/>
        <input className="input-aii" placeholder="Contact person" value={f.contact_person} onChange={e => setF({ ...f, contact_person: e.target.value })}/>
        <input className="input-aii" placeholder="Country *" value={f.country} onChange={e => setF({ ...f, country: e.target.value })}/>
        <input className="input-aii" placeholder="City *" value={f.city} onChange={e => setF({ ...f, city: e.target.value })}/>
        <input className="input-aii" placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })}/>
        <input className="input-aii sm:col-span-2" placeholder="Address" value={f.address} onChange={e => setF({ ...f, address: e.target.value })}/>
        <input className="input-aii" placeholder="Website" value={f.website} onChange={e => setF({ ...f, website: e.target.value })}/>
        <input className="input-aii sm:col-span-3" placeholder="Notes" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}/>
      </div>
      <button onClick={submit} disabled={busy} data-testid="pa-submit" className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5"><Plus size={14}/> {busy ? "Creating…" : "Create partner"}</button>
    </div>
  );
}

function UploadTemplatesPanel({ partner }) {
  const [prog, setProg] = useState("");
  const [wknd, setWknd] = useState("");
  const [busy, setBusy] = useState(false);
  const DEFAULT_PROG = "name, email, telephone, dob, gender, program, destination, city, duration_weeks, start_date, end_date, notes";
  const DEFAULT_WKND = "customer, email, telephone, destinations, weekend_trips, start_dates, end_dates, notes";

  const load = async () => {
    try {
      const { data } = await api.get("/partner-upload-templates");
      const p = (data || []).find(x => x.partner_id === partner.id && x.kind === "program");
      const w = (data || []).find(x => x.partner_id === partner.id && x.kind === "weekend");
      setProg(p ? p.columns.join(", ") : "");
      setWknd(w ? w.columns.join(", ") : "");
    } catch (err) { console.debug("partner-upload-templates load failed:", err?.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async (kind, csv) => {
    const cols = csv.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
    if (cols.length === 0) return toast.error("Add at least one column");
    setBusy(true);
    try {
      await api.post("/partner-upload-templates", { partner_id: partner.id, kind, columns: cols });
      toast.success(`${kind === "program" ? "Program" : "Weekend"} template saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-[color:var(--aii-border)] pt-3 space-y-3 bg-[color:var(--aii-bg)] p-3 rounded" data-testid={`tpl-panel-${partner.id}`}>
      <div className="text-xs uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold flex items-center gap-1.5">
        <FileText size={12} className="text-brand"/> Upload templates for {partner.name}
      </div>
      <p className="text-[11px] text-[color:var(--aii-muted)]">Define custom columns partners will see in their Excel template (comma-separated, lower-case). Blank = use defaults. Partner uploads validate against this template and land in Program Bookings / Weekend Bookings.</p>

      <div>
        <div className="text-[11px] uppercase font-semibold text-[color:var(--aii-text-2)] mb-1">Program bookings columns</div>
        <textarea data-testid={`tpl-prog-${partner.id}`} value={prog} onChange={e => setProg(e.target.value)} rows={2} className="input-aii text-sm w-full" placeholder={DEFAULT_PROG}/>
        <div className="flex items-center gap-2 mt-1">
          <button onClick={() => save("program", prog)} disabled={busy} data-testid={`tpl-prog-save-${partner.id}`} className="text-xs px-3 py-1 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50">Save</button>
          <button onClick={() => setProg(DEFAULT_PROG)} className="text-xs px-3 py-1 border border-[color:var(--aii-border)] rounded">Reset to default</button>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase font-semibold text-[color:var(--aii-text-2)] mb-1">Weekend bookings columns</div>
        <textarea data-testid={`tpl-wknd-${partner.id}`} value={wknd} onChange={e => setWknd(e.target.value)} rows={2} className="input-aii text-sm w-full" placeholder={DEFAULT_WKND}/>
        <div className="flex items-center gap-2 mt-1">
          <button onClick={() => save("weekend", wknd)} disabled={busy} data-testid={`tpl-wknd-save-${partner.id}`} className="text-xs px-3 py-1 bg-brand text-white rounded hover:bg-brand-dark disabled:opacity-50">Save</button>
          <button onClick={() => setWknd(DEFAULT_WKND)} className="text-xs px-3 py-1 border border-[color:var(--aii-border)] rounded">Reset to default</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// PartnerPrearrivalCard — per-partner pre-arrival doc list + upload.
// Uses the shared `/pre-arrival-docs` API; staff see every doc so we
// filter client-side to those scoped to THIS partner. Upload posts
// with partner_ids = [pid] so the doc is exclusive to this partner.
// ------------------------------------------------------------
function PartnerPrearrivalCard({ partner }) {
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("file"); // file | youtube
  const [ytUrl, setYtUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

  const load = async () => {
    try {
      const { data } = await api.get("/pre-arrival-docs");
      setDocs(data || []);
    } catch (e) { console.debug("prearrival load failed:", e?.message); }
  };
  useEffect(() => { load(); }, [partner.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const scoped = docs.filter(d => Array.isArray(d.partner_ids) && d.partner_ids.includes(partner.id));
  const globals = docs.filter(d => !Array.isArray(d.partner_ids) || d.partner_ids.length === 0);

  const upload = async () => {
    if (!title.trim()) return toast.error("Give it a title");
    if (mode === "file" && !file) return toast.error("Pick a file");
    if (mode === "youtube" && !ytUrl.trim()) return toast.error("Paste the YouTube URL");
    if (mode === "youtube" && !/(youtube\.com|youtu\.be)\//i.test(ytUrl.trim())) return toast.error("That doesn't look like a YouTube URL");
    setBusy(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    if (mode === "file") fd.append("file", file);
    else fd.append("youtube_url", ytUrl.trim());
    fd.append("partner_ids", partner.id);
    try {
      await api.post("/pre-arrival-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — partner participants will see it in their portal");
      setTitle(""); setFile(null); setYtUrl("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this document from the participant portal?")) return;
    try { await api.delete(`/pre-arrival-docs/${id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const untarget = async (id) => {
    const d = docs.find(x => x.id === id);
    const next = (d?.partner_ids || []).filter(x => x !== partner.id);
    try {
      await api.patch(`/pre-arrival-docs/${id}`, { partner_ids: next });
      toast.success(next.length ? "Un-scoped from this partner" : "Now global");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const fmtSize = (b) => (b > 1_000_000 ? `${(b/1_000_000).toFixed(1)} MB` : b > 1000 ? `${Math.round(b/1000)} KB` : `${b} B`);

  return (
    <div className="p-4 bg-[color:var(--aii-bg)]/60 border-t border-[color:var(--aii-border)]" data-testid={`pa-prearrival-card-${partner.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <BookOpen size={14} className="text-brand"/>
        <div className="text-sm font-semibold">Pre-arrival documents for {partner.name}</div>
      </div>
      <p className="text-[11px] text-[color:var(--aii-muted)] mb-3">Only participants booked with this partner will see uploads made here. PDFs, PPTs, DOCs up to 15 MB — or paste a YouTube link.</p>

      <div className="grid sm:grid-cols-[auto_1fr_auto_auto] gap-2 items-end mb-3">
        <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden">
          <button type="button" onClick={() => setMode("file")}
                  data-testid={`pa-prearrival-mode-file-${partner.id}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold ${mode === "file" ? "bg-brand text-white" : "bg-white"}`}>File</button>
          <button type="button" onClick={() => setMode("youtube")}
                  data-testid={`pa-prearrival-mode-yt-${partner.id}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold ${mode === "youtube" ? "bg-brand text-white" : "bg-white"}`}>YouTube</button>
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)}
               data-testid={`pa-prearrival-title-${partner.id}`}
               placeholder={mode === "youtube" ? "Video title" : "Document title"}
               className="input-aii text-sm"/>
        {mode === "file" ? (
          <label className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white hover:bg-[color:var(--aii-green-tint)] cursor-pointer flex items-center gap-1 whitespace-nowrap">
            <Upload size={12} className="text-brand"/> {file ? <span className="truncate max-w-[140px]">{file.name}</span> : "Choose file"}
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden"
                   data-testid={`pa-prearrival-file-${partner.id}`}
                   onChange={e => setFile(e.target.files?.[0] || null)}/>
          </label>
        ) : (
          <input value={ytUrl} onChange={e => setYtUrl(e.target.value)}
                 data-testid={`pa-prearrival-yt-${partner.id}`}
                 placeholder="https://youtube.com/watch?v=..."
                 className="input-aii text-sm min-w-[200px]"/>
        )}
        <button onClick={upload} disabled={busy}
                data-testid={`pa-prearrival-submit-${partner.id}`}
                className="bg-brand text-white font-semibold px-3 py-1.5 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5 text-xs whitespace-nowrap">
          <Upload size={12}/> {busy ? "Saving…" : "Upload"}
        </button>
      </div>

      {scoped.length === 0 ? (
        <div className="text-xs text-[color:var(--aii-muted)] italic border border-dashed border-[color:var(--aii-border)] rounded p-3 text-center">
          No partner-scoped documents yet.{globals.length > 0 && <> {globals.length} global document{globals.length === 1 ? "" : "s"} still reach this partner.</>}
        </div>
      ) : (
        <div className="space-y-1.5">
          {scoped.map(d => (
            <div key={d.id} data-testid={`pa-prearrival-row-${d.id}`}
                 className="flex items-center gap-2 p-2 bg-white border border-[color:var(--aii-border)] rounded">
              <span className="text-lg">{d.kind === "youtube" ? "▶️" : (d.content_type || "").includes("pdf") ? "📄" : "📎"}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{d.title}</div>
                <div className="text-[10px] text-[color:var(--aii-muted)] truncate">
                  {d.kind === "youtube" ? d.youtube_url : `${d.filename || "file"}${d.size ? ` · ${fmtSize(d.size)}` : ""}`}
                  {d.uploaded_by_name ? ` · by ${d.uploaded_by_name}` : ""}
                </div>
              </div>
              {d.kind === "youtube" ? (
                <a href={d.youtube_url} target="_blank" rel="noopener noreferrer"
                   className="text-xs px-2 py-1 border border-brand text-brand rounded font-semibold hover:bg-[color:var(--aii-green-tint)]">Watch</a>
              ) : (
                <a href={`${API}/files/${d.file_id}?auth=${getToken()}`} target="_blank" rel="noopener noreferrer"
                   className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                  <Download size={11}/> Open
                </a>
              )}
              <button onClick={() => untarget(d.id)} title="Remove this partner from the doc's audience"
                      data-testid={`pa-prearrival-untarget-${d.id}`}
                      className="text-[color:var(--aii-muted)] hover:text-amber-700 p-1"><X size={13}/></button>
              <button onClick={() => del(d.id)} title="Delete document" data-testid={`pa-prearrival-del-${d.id}`}
                      className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><Trash2 size={12}/></button>
            </div>
          ))}
        </div>
      )}

      {globals.length > 0 && (
        <div className="mt-3 text-[10px] text-[color:var(--aii-muted)]" data-testid={`pa-prearrival-globals-${partner.id}`}>
          Plus {globals.length} global document{globals.length === 1 ? "" : "s"} everyone sees (manage in Settings → Pre-arrival Instructions).
        </div>
      )}
    </div>
  );
}


export default function PartnerAdmin() {
  const { user } = useAuth();
  const isFounder = user.effective_role === "FOUNDER";
  const [accounts, setAccounts] = useState([]);
  const [apps, setApps] = useState([]);
  // Persist the "show processed" toggle in memory so a page refresh returns
  // to the clean default view (approved-and-priced hidden).
  const [showProcessedApps, setShowProcessedApps] = useState(false);
  // Bulk-approve state: ids ticked in the panel checkbox column.
  const [selectedAppIds, setSelectedAppIds] = useState(() => new Set());
  const [weekend, setWeekend] = useState([]);
  const [priceForm, setPriceForm] = useState({});
  const [expanded, setExpanded] = useState(new Set());
  const [expandedTpl, setExpandedTpl] = useState(new Set());
  const [sortByRevenue, setSortByRevenue] = useState(false);
  const [breakdown, setBreakdown] = useState(null); // { open, partner, loading, data }
  const [brandTarget, setBrandTarget] = useState(null);
  // Type-to-confirm state for "Reset all data" on a partner. Owned by
  // this page so it doesn't collide with the delete-account confirm.
  const [resetTarget, setResetTarget] = useState(null); // partner row
  const [resetType, setResetType] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const togglePricing = (id) => setExpanded(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleTpl = (id) => setExpandedTpl(s => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const reload = async () => {
    const [aR, appR, wR] = await Promise.all([
      api.get("/partner-accounts").catch(() => ({ data: [] })),
      api.get("/partner-applications").catch(() => ({ data: [] })),
      api.get("/partner-weekend-bookings").catch(() => ({ data: [] })),
    ]);
    setAccounts(aR.data); setApps(appR.data); setWeekend(wR.data);
  };
  useEffect(() => { reload(); }, []);

  const resetPwd = async (p) => {
    try {
      const { data } = await api.post(`/partner-accounts/${p.id}/reset-password`);
      try { await navigator.clipboard.writeText(`Email: ${data.email}\nPassword: ${data.password}`); toast.success("New password copied"); }
      catch { toast.success(`New password: ${data.password}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delAcc = async (p) => {
    if (!window.confirm(`Delete partner ${p.name}?`)) return;
    try { await api.delete(`/partner-accounts/${p.id}`); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Reset every piece of transactional data on this partner — bookings,
  // weekend bookings, sales, invoices, applications, payments — while
  // keeping the login + branding + program catalog rows intact. Wrapped
  // in a Recycle Bin bundle so a mistake is one-tap restorable.
  const doReset = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const { data } = await api.post(`/partner-accounts/${resetTarget.id}/reset-data`);
      toast.success(`Reset — ${data.doc_count} row${data.doc_count === 1 ? "" : "s"} archived (${data.program_bookings_cleared} program + ${data.weekend_bookings_cleared} weekend bookings). Restore from Recycle Bin.`);
      setResetTarget(null); setResetType("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetBusy(false); }
  };

  const approve = async (a) => { try { await api.post(`/partner-applications/${a.id}/approve`); toast.success("Approved — booking created"); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const reject = async (a) => { const reason = window.prompt("Reject reason:"); if (reason === null) return; try { await api.post(`/partner-applications/${a.id}/reject`, { reason }); toast.success("Rejected"); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const setPrice = async (a) => { const p = parseFloat(priceForm[a.id]); if (!p) return toast.error("Enter a price"); try { await api.post(`/partner-applications/${a.id}/set-price`, { price: p, currency: "USD" }); toast.success("Price set · invoice created"); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const sendInv = async (a) => { try { await api.post(`/partner-applications/${a.id}/send-invoice`); toast.success("Invoice emailed to partner"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const approveWeekend = async (w) => { try { await api.post(`/partner-weekend-bookings/${w.id}/approve`); toast.success("Approved → moved to Weekend Bookings"); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const rejectWeekend = async (w) => { try { await api.post(`/partner-weekend-bookings/${w.id}/reject`); toast.success("Rejected"); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  // Sum every currency into a single number so the sort compares fairly.
  // Founders will always see the pill breakdown next to it — this is only
  // used to bubble top earners to the top of the table.
  const revenueScore = (p) => Object.values(p.total_revenue_by_currency || {}).reduce((s, v) => s + Number(v || 0), 0);

  const sortedAccounts = useMemo(() => {
    // Pre-compute the sorted revenue-pill entries once per partner so
    // the row render is a cheap array iterate (no per-render
    // Object.entries + sort). Attaching to a new `_revenuePills` field
    // keeps the shape backwards-compatible with anything else that
    // reads the raw `total_revenue_by_currency` map.
    const withPills = accounts.map(p => ({
      ...p,
      _revenuePills: Object.entries(p.total_revenue_by_currency || {})
        .sort(([a], [b]) => a.localeCompare(b)),
    }));
    if (!sortByRevenue) return withPills;
    return withPills.sort((a, b) => revenueScore(b) - revenueScore(a));
  }, [accounts, sortByRevenue]);

  // Memoize the modal's currency-totals list — it's rendered inside a
  // conditional and would otherwise re-sort on every parent re-render
  // while the modal is open.
  const breakdownTotalPills = useMemo(() => (
    Object.entries(breakdown?.data?.totals_by_currency || {})
      .sort(([a], [b]) => a.localeCompare(b))
  ), [breakdown?.data?.totals_by_currency]);

  const openBreakdown = async (p) => {
    setBreakdown({ open: true, partner: p, loading: true, data: null });
    try {
      const { data } = await api.get(`/partner-accounts/${p.id}/revenue-breakdown`);
      setBreakdown({ open: true, partner: p, loading: false, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load revenue breakdown");
      setBreakdown(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="partner-admin">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Sending Organisations</div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Handshake className="text-brand"/> Partner Accounts</h1>
        <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Create logins for universities, sending orgs and representatives. Review their applications and weekend tour requests below.</p>
      </div>

      <CreateAccount onCreated={reload}/>

      {/* Cancellation requests + deletion log — surfaces partner tickets
          and lets Founder spot cancellation trends without leaving this
          page. */}
      <FounderCancellations />

      <div className="aii-card overflow-x-auto">
        <div className="flex items-center justify-between p-3 border-b border-[color:var(--aii-border)]">
          <div className="text-xs text-[color:var(--aii-muted)]">{accounts.length} partner{accounts.length === 1 ? "" : "s"}</div>
          <button
            data-testid="pa-sort-revenue"
            onClick={() => setSortByRevenue(s => !s)}
            className={`text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 font-semibold ${sortByRevenue ? "bg-brand text-white border-brand" : "bg-white text-[color:var(--aii-text-2)] border-[color:var(--aii-border)] hover:bg-slate-50"}`}
            title="Toggle sort by total revenue">
            <ArrowUpDown size={12}/> {sortByRevenue ? "Sorted by revenue ↓" : "Sort by revenue"}
          </button>
        </div>
        <table className="aii-table" style={{ minWidth: 720 }}>
          <thead><tr><th>Partner</th><th>Email</th><th>Location</th><th>Contact</th><th>Total revenue</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {sortedAccounts.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[color:var(--aii-muted)]">No partner accounts yet.</td></tr>}
            {sortedAccounts.map(p => (
              <React.Fragment key={p.id}>
                <tr data-testid={`pa-row-${p.id}`}>
                  <td className="font-medium">
                    <div className="inline-flex items-center gap-1.5">
                      {p.name}
                      {p.locked && (
                        <span data-testid={`pa-locked-${p.id}`}
                              title={`Emergency-locked${p.locked_by ? ` by ${p.locked_by}` : ""}${p.locked_at ? ` on ${p.locked_at.slice(0,10)}` : ""}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded font-bold">
                          <Lock size={9}/> LOCKED
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-xs" data-testid={`pa-email-${p.id}`}><MailLink email={p.email}/></td>
                  <td className="text-xs">{p.partner_profile?.city}, {p.partner_profile?.country}</td>
                  <td className="text-xs" data-testid={`pa-contact-${p.id}`}>
                    <span>{p.partner_profile?.contact_person || "—"}</span>
                    {p.partner_profile?.phone && <> · <WhatsAppLink phone={p.partner_profile.phone}/></>}
                  </td>
                  <td className="text-xs font-semibold text-brand whitespace-nowrap" data-testid={`pa-revenue-${p.id}`}>
                    {Object.keys(p.total_revenue_by_currency || {}).length === 0
                      ? <span className="text-[color:var(--aii-muted)] font-normal">—</span>
                      : (
                          <button
                            data-testid={`pa-revenue-open-${p.id}`}
                            onClick={() => openBreakdown(p)}
                            className="text-brand font-semibold underline decoration-dotted decoration-brand/40 underline-offset-2 hover:decoration-solid hover:text-brand-dark"
                            title="Click to see every booking behind this total">
                            {p._revenuePills.map(([c, v]) =>
                              `${CUR_SYM[c] || c}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                            ).join(" · ")}
                          </button>
                        )
                    }
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => resetPwd(p)} data-testid={`pa-reset-${p.id}`} className="text-xs px-2 py-1 border rounded flex items-center gap-1"><KeyRound size={11}/> Set ID & Password</button>
                      <CredentialEditorButton user={p} onDone={reload}/>
                      {isFounder && (
                        <button onClick={() => setBrandTarget(p)} data-testid={`pa-brand-${p.id}`}
                          title="Edit this partner's white-label landing page"
                          className="text-xs px-2 py-1 border border-[color:var(--aii-border)] text-brand rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                          <Palette size={11}/> Brand
                        </button>
                      )}
                      <button
                        data-testid={`pa-prearrival-toggle-${p.id}`}
                        onClick={() => setExpanded(s => { const n = new Set(s); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })}
                        className="text-xs px-2 py-1 border border-[color:var(--aii-border)] text-brand rounded hover:bg-[color:var(--aii-green-tint)]"
                        title="Attach pre-arrival documents (PDF/PPT/YouTube) for this partner">
                        Pre-arrival
                      </button>
                      {isFounder && (
                        <button
                          data-testid={`pa-reset-data-${p.id}`}
                          onClick={() => { setResetTarget(p); setResetType(""); }}
                          title="Wipe every booking, sale, payment, and invoice on this partner (login + branding stay). Restore-able from Recycle Bin."
                          className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1">
                          <RotateCcw size={11}/> Reset
                        </button>
                      )}
                      {isFounder && <button onClick={() => delAcc(p)} data-testid={`pa-del-${p.id}`} className="text-red-600 p-1"><Trash2 size={13}/></button>}
                    </div>
                  </td>
                </tr>
                {expanded.has(p.id) && (
                  <tr data-testid={`pa-prearrival-row-${p.id}`}>
                    <td colSpan={6} className="p-0">
                      <PartnerPrearrivalCard partner={p}/>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        {/* Filter out fully-processed applications (approved AND priced) by
            default — once the founder has set a price the app becomes a
            program booking and doesn't need to reappear here. Un-hide via
            the toggle for auditing / re-pricing. Freshly-approved apps
            without a price are ALWAYS visible so the price step isn't lost. */}
        {(() => {
          const isDone = (a) => a.status === "approved" && Number(a.price || 0) > 0;
          const visibleApps = showProcessedApps ? apps : apps.filter(a => !isDone(a));
          const processedCount = apps.filter(isDone).length;
          return (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mt-4 mb-2">
                <h2 className="font-display font-semibold text-lg">Applications ({visibleApps.length})</h2>
                <div className="flex items-center gap-2">
                  {selectedAppIds.size > 0 && isFounder && (
                    <button
                      data-testid="app-bulk-approve"
                      onClick={async () => {
                        const ids = [...selectedAppIds];
                        if (!window.confirm(`Approve ${ids.length} selected application${ids.length===1?"":"s"} in one shot?`)) return;
                        try {
                          const { data } = await api.post("/partner-applications/bulk-approve", { ids });
                          toast.success(`Approved ${data.approved} of ${data.requested}`);
                          setSelectedAppIds(new Set());
                          await reload();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
                      }}
                      className="text-xs px-3 py-1.5 bg-brand text-white rounded font-semibold hover:bg-brand-dark">
                      ✓ Approve {selectedAppIds.size} selected
                    </button>
                  )}
                  {processedCount > 0 && (
                    <button
                      onClick={() => setShowProcessedApps(v => !v)}
                      data-testid="app-toggle-processed"
                      className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded font-semibold text-[color:var(--aii-text-2)] hover:bg-slate-50">
                      {showProcessedApps ? `Hide ${processedCount} processed` : `Show ${processedCount} processed`}
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {visibleApps.length === 0 && <div className="aii-card p-6 text-center text-sm text-[color:var(--aii-muted)]">{apps.length === 0 ? "No applications yet." : "All applications are approved & priced — nothing pending."}</div>}
                {visibleApps.map(a => (
            <div key={a.id} data-testid={`app-${a.id}`} className="aii-card p-4">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {isFounder && a.status === "pending" && (
                    <input
                      type="checkbox"
                      data-testid={`app-check-${a.id}`}
                      checked={selectedAppIds.has(a.id)}
                      onChange={(e) => {
                        const next = new Set(selectedAppIds);
                        if (e.target.checked) next.add(a.id); else next.delete(a.id);
                        setSelectedAppIds(next);
                      }}
                      className="mt-2 w-4 h-4 accent-brand cursor-pointer flex-shrink-0"
                      title="Tick to include in Bulk approve"/>
                  )}
                  <ApplicantAvatar picId={a.profile_pic_id} name={a.name} size={44}/>
                  <div className="min-w-0">
                    <div className="font-semibold">{a.name} <span className="text-xs text-[color:var(--aii-muted)] font-normal">via {a.partner_name}</span></div>
                    <div className="text-xs text-[color:var(--aii-muted)]">{a.program}{a.project ? ` · ${a.project}` : ""} · {a.destination}/{a.city} · {a.duration_weeks} weeks · {a.start_date} → {a.end_date}</div>
                    <div className="text-xs mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`app-contact-${a.id}`}>
                      <MailLink email={a.email}/>
                      {a.phone && <><span className="text-[color:var(--aii-muted)]">·</span><WhatsAppLink phone={a.phone}/></>}
                    </div>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-semibold uppercase ${a.status === "approved" ? "bg-emerald-100 text-emerald-800" : a.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
              </div>
              {isFounder && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {a.status === "pending" && (
                    <>
                      <button onClick={() => approve(a)} data-testid={`app-approve-${a.id}`} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1"><Check size={12}/> Approve</button>
                      <button onClick={() => reject(a)} data-testid={`app-reject-${a.id}`} className="text-xs px-3 py-1.5 border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1"><X size={12}/> Reject</button>
                    </>
                  )}
                  {a.status === "approved" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input type="number" placeholder="Price USD" value={priceForm[a.id] || a.price || ""} onChange={e => setPriceForm({ ...priceForm, [a.id]: e.target.value })} className="input-aii text-sm" style={{ maxWidth: 140 }}/>
                      <button onClick={() => setPrice(a)} data-testid={`app-price-${a.id}`} className="text-xs px-3 py-1.5 bg-brand text-white rounded flex items-center gap-1"><DollarSign size={12}/> Set price</button>
                      {a.invoice_id && <button onClick={() => sendInv(a)} data-testid={`app-invoice-${a.id}`} className="text-xs px-3 py-1.5 border border-brand text-brand rounded flex items-center gap-1"><Send size={12}/> Email invoice</button>}
                      {a.price != null && <span className="text-sm font-semibold text-brand">{USD(a.price)}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        </>
      );
        })()}
      </div>

      <div>
        <h2 className="font-display font-semibold text-lg mt-4 mb-2">Partner-submitted weekend tours ({weekend.filter(w => w.status === "pending").length} pending)</h2>
        <div className="space-y-2">
          {weekend.map(w => (
            <div key={w.id} className="aii-card p-4 flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold text-sm">{w.participant_names}</div>
                <div className="text-xs text-[color:var(--aii-muted)]">{w.destinations} · {w.weekend_trips} · {w.start_dates} → {w.end_dates} · via {w.partner_name}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded uppercase ${w.status === "approved" ? "bg-emerald-100 text-emerald-800" : w.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{w.status}</span>
                {w.status === "pending" && (
                  <>
                    <button onClick={() => approveWeekend(w)} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded"><Check size={11}/></button>
                    <button onClick={() => rejectWeekend(w)} className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded"><X size={11}/></button>
                  </>
                )}
              </div>
            </div>
          ))}
          {weekend.length === 0 && <div className="aii-card p-6 text-center text-sm text-[color:var(--aii-muted)]">No weekend requests yet.</div>}
        </div>
      </div>

      {breakdown?.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setBreakdown(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="pa-revenue-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Receipt size={16} className="text-brand"/> Revenue breakdown · {breakdown.partner?.name}</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Every priced booking (program + weekend) attributed to this partner. Sorted newest first.</p>
                {breakdownTotalPills.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="pa-revenue-modal-totals">
                    {breakdownTotalPills.map(([c, v]) => (
                      <span key={c} className="px-2 py-1 text-xs rounded bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-semibold">
                        {CUR_SYM[c] || c}{Number(v).toLocaleString()} <span className="text-[10px] opacity-70">{c}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setBreakdown(null)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {breakdown.loading && <div className="p-10 text-center text-sm text-[color:var(--aii-muted)]">Loading breakdown…</div>}
              {!breakdown.loading && (breakdown.data?.rows || []).length === 0 && (
                <div className="p-10 text-center text-sm text-[color:var(--aii-muted)] italic" data-testid="pa-revenue-modal-empty">
                  No priced bookings recorded for this partner yet.
                </div>
              )}
              {!breakdown.loading && (breakdown.data?.rows || []).length > 0 && (
                <table className="w-full text-xs" data-testid="pa-revenue-modal-table">
                  <thead className="bg-[color:var(--aii-bg)] sticky top-0"><tr className="text-left">
                    <th className="px-3 py-2">Kind</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Program / Tour</th>
                    <th className="px-3 py-2">Start</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr></thead>
                  <tbody>
                    {breakdown.data.rows.map((r, i) => (
                      <tr key={`bd-${i}`} className="border-t border-[color:var(--aii-border)]/50" data-testid={`pa-revenue-row-${i}`}>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${r.kind === "program" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`}>{r.kind}</span>
                        </td>
                        <td className="px-3 py-1.5 truncate max-w-[10rem]">{r.customer}</td>
                        <td className="px-3 py-1.5 truncate max-w-[14rem]">{r.label}</td>
                        <td className="px-3 py-1.5 text-[color:var(--aii-muted)] whitespace-nowrap">{r.start_date || "—"}</td>
                        <td className="px-3 py-1.5 text-[color:var(--aii-muted)]">{r.matched_by || "—"}</td>
                        <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap text-brand">
                          {CUR_SYM[r.currency] || r.currency}{Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end">
              <button data-testid="pa-revenue-modal-close" onClick={() => setBreakdown(null)} className="px-4 py-2 text-sm bg-brand text-white rounded-md font-semibold hover:bg-brand-dark">Close</button>
            </div>
          </div>
        </div>
      )}
      {brandTarget && (
        <PartnerBrandModal partner={brandTarget}
          onClose={() => setBrandTarget(null)}
          onSaved={reload}/>
      )}

      {resetTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !resetBusy && setResetTarget(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="pa-reset-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] bg-red-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-red-800">
                <AlertOctagon size={18} className="text-red-600"/> Reset all data for {resetTarget.name}?
              </h3>
              <p className="text-xs text-red-800 mt-1">
                Wipes every program booking, weekend booking, sale, invoice, payment, application,
                arrival and C-form tied to <b>{resetTarget.name}</b>. The login, branding profile
                and partner-programs catalog stay put. Everything gets snapshotted into ONE
                Recycle Bin bundle you can restore in a tap if fired by mistake.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <label className="text-xs font-semibold text-[color:var(--aii-text-1)] block">
                To confirm, type <code className="text-red-700 bg-red-50 px-1 rounded">RESET {resetTarget.name}</code>
              </label>
              <input
                data-testid="pa-reset-confirm-input"
                value={resetType}
                onChange={e => setResetType(e.target.value)}
                autoFocus
                placeholder={`RESET ${resetTarget.name}`}
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm font-mono"/>
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setResetTarget(null)} disabled={resetBusy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="pa-reset-confirm-submit"
                onClick={doReset}
                disabled={resetBusy || resetType.trim() !== `RESET ${resetTarget.name}`}
                className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                <RotateCcw size={13}/> {resetBusy ? "Resetting…" : "Reset all data"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
